/**
 * ManagerPinGate — small helper that runs any critical action through a PIN check.
 *
 * Usage:
 *   const gate = useManagerPinGate();
 *   ...
 *   const onToggleAI = async () => {
 *     const ok = await gate.request("Turn off the AI receptionist");
 *     if (!ok) return;
 *     // proceed with the action
 *   };
 *   ...
 *   return <>{gate.modal}...</>;
 *
 * If no PIN is set on the business, `request()` returns true immediately —
 * gating stays opt-in per operator. The unlock persists in memory for 5 minutes
 * per session so a manager doesn't retype during a busy shift.
 */
import { useEffect, useRef, useState } from "react";
import { Lock, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { toast } from "sonner";

const UNLOCK_WINDOW_MS = 5 * 60 * 1000; // 5 min

export function useManagerPinGate() {
  const [pinSet, setPinSet] = useState(null); // null=loading, true/false=known
  const [askOpen, setAskOpen] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [action, setAction] = useState("");
  const resolveRef = useRef(null);
  const unlockUntil = useRef(0);

  useEffect(() => {
    api.get("/manager-pin/status").then((r) => setPinSet(!!r.data.is_set)).catch(() => setPinSet(false));
  }, []);

  const request = (actionLabel = "this action") => {
    // No PIN set → let it through, but tell the operator once.
    if (pinSet === false) return Promise.resolve(true);
    // Recently unlocked → skip the modal.
    if (Date.now() < unlockUntil.current) return Promise.resolve(true);
    setAction(actionLabel);
    setPinInput("");
    setErr("");
    setAskOpen(true);
    return new Promise((resolve) => { resolveRef.current = resolve; });
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/manager-pin/verify", { pin: pinInput });
      if (data.ok) {
        unlockUntil.current = Date.now() + UNLOCK_WINDOW_MS;
        setAskOpen(false);
        resolveRef.current?.(true);
      } else {
        setErr("Wrong PIN. Try again.");
        setPinInput("");
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Verification failed.";
      setErr(msg);
    }
    setBusy(false);
  };

  const cancel = () => {
    setAskOpen(false);
    resolveRef.current?.(false);
  };

  const modal = askOpen && (
    <div
      className="fixed inset-0 z-[60] bg-[#121914]/70 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="manager-pin-modal"
      onClick={cancel}
    >
      <div className="bg-[#F7F6F2] rounded-2xl shadow-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D96C5B] flex items-center justify-center">
              <Lock size={20} color="#fff" weight="fill" />
            </div>
            <div>
              <div className="font-heading font-extrabold text-lg text-[#121914]">Manager PIN required</div>
              <div className="text-xs text-[#5C6B61] mt-0.5">To {action.toLowerCase()}</div>
            </div>
          </div>
          <button type="button" onClick={cancel} className="text-[#5C6B61] hover:text-[#121914]" data-testid="pin-modal-close">
            <X size={20} weight="bold" />
          </button>
        </div>
        <input
          type="password"
          inputMode="numeric"
          autoFocus
          maxLength={8}
          value={pinInput}
          onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ""))}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="Enter 4–8 digit PIN"
          className="input w-full text-2xl tracking-widest text-center"
          data-testid="pin-modal-input"
        />
        {err && <div className="text-xs text-[#D96C5B] font-semibold mt-2" data-testid="pin-modal-err">{err}</div>}
        <p className="text-xs text-[#5C6B61] mt-3 leading-snug">
          Once entered, this device stays unlocked for 5 minutes so managers aren't retyping during a rush.
        </p>
        <div className="flex justify-end gap-2 mt-5">
          <button type="button" onClick={cancel} className="text-sm font-semibold text-[#5C6B61] px-4 py-2 rounded-full hover:bg-[#E1DFD5]" data-testid="pin-modal-cancel">Cancel</button>
          <button type="button" onClick={submit} disabled={busy || pinInput.length < 4} className="btn-primary text-sm !py-2 !px-4" data-testid="pin-modal-submit">
            {busy ? "Checking…" : "Unlock"}
          </button>
        </div>
      </div>
    </div>
  );

  return { modal, request, pinSet, refresh: () => api.get("/manager-pin/status").then((r) => setPinSet(!!r.data.is_set)) };
}
